import request from "../../utils/request";

export interface PerformanceSnapshotView {
  id: number;
  employeeId: string;
  employeeName: string;
  employeeImageUrl: string | null;
  period: string;
  strengthsSummary: string | null;
  goalsSummary: string | null;
  checkInsSummary: string | null;
  recognitionSummary: string | null;
  overallScore: number | null;
  status: number; // 1=Draft, 2=Submitted, 3=Acknowledged
  decisionsSummary: string | null;
  narrativePromptsSummary: string | null;
  strengthsValidation: string | null;
  createdOn: string;
  updatedOn: string | null;
}

export interface SnapshotUpdateInput {
  decisionsSummary?: string | null;
  narrativePromptsSummary?: string | null;
  strengthsValidation?: string | null;
}

export interface ComparativeContextView {
  insufficientData: boolean;
  snapshotCount: number;
  distributions: Record<string, { decision: string; count: number }[]>;
  patternBreaks: {
    snapshotId: number;
    employeeName: string;
    role: string;
    decision: string;
    patternContradiction: string;
    details: string;
  }[];
  roleSignals: {
    category: string;
    employeeSignal: string;
    peerPattern: string;
  }[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractPayload = <T>(response: any): T => {
  if (!response) throw new Error("No response received");
  if (response.payload !== undefined) return response.payload;
  if (response.data && response.data.payload !== undefined) return response.data.payload;
  return response;
};

export async function generateSnapshot(employeeId: string): Promise<PerformanceSnapshotView> {
  const response = await request.post(`PerformanceSnapshot/Generate/${employeeId}`);
  return extractPayload<PerformanceSnapshotView>(response.data);
}

export async function getSnapshot(snapshotId: number): Promise<PerformanceSnapshotView> {
  const response = await request.get(`PerformanceSnapshot/${snapshotId}`);
  return extractPayload<PerformanceSnapshotView>(response.data);
}

export async function getEmployeeSnapshots(employeeId: string): Promise<PerformanceSnapshotView[]> {
  const response = await request.get(`PerformanceSnapshot/Employee/${employeeId}`);
  return extractPayload<PerformanceSnapshotView[]>(response.data);
}

export async function submitSnapshot(snapshotId: number): Promise<PerformanceSnapshotView> {
  const response = await request.put(`PerformanceSnapshot/Submit/${snapshotId}`);
  return extractPayload<PerformanceSnapshotView>(response.data);
}

export async function acknowledgeSnapshot(snapshotId: number): Promise<PerformanceSnapshotView> {
  const response = await request.put(`PerformanceSnapshot/Acknowledge/${snapshotId}`);
  return extractPayload<PerformanceSnapshotView>(response.data);
}

export async function updateSnapshot(snapshotId: number, input: SnapshotUpdateInput): Promise<PerformanceSnapshotView> {
  const response = await request.put(`PerformanceSnapshot/Update/${snapshotId}`, input);
  return extractPayload<PerformanceSnapshotView>(response.data);
}

export async function getComparativeContext(period: string, employeeId?: string): Promise<ComparativeContextView> {
  const params = employeeId ? `?employeeId=${employeeId}` : '';
  const response = await request.get(`PerformanceSnapshot/ComparativeContext/${period}${params}`);
  return extractPayload<ComparativeContextView>(response.data);
}
